import { collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, serverTimestamp, startAfter, DocumentSnapshot, QueryDocumentSnapshot, getDocsFromCache, getDocsFromServer, onSnapshot, Unsubscribe, Timestamp } from 'firebase/firestore';
import { db, getUserId } from './firebase';
import { InventoryItem, SilverItem, WatchItem, BaseItem, ItemType, ItemStatus, Currency, SalesTransaction } from '../types';
import { InventoryFilterValues, ActiveRangeFilter, FilterOptionAll } from '../types/filters';
import { withAuthentication } from './authService';
import { getExchangeRateForDate, getCurrentExchangeRate } from './exchangeRateService';
import { createSalesTransaction } from './transactionService';

// Get flat collection references
const getItemsCollection = () => {
  if (!db) throw new Error('Firestore yüklenemedi');
  return collection(db, 'items');
};

const getItemDocument = (itemId: string) => {
  if (!db) throw new Error('Firestore yüklenemedi');
  return doc(db, `items/${itemId}`);
};

/**
 * Get all items with pagination and filtering support
 */
const _getAllItems = async (
  pageSize: number = 10,
  pageNumber: number = 1,
  lastVisibleDoc?: QueryDocumentSnapshot,
  filters?: InventoryFilterValues,
  activeRangeFilter?: ActiveRangeFilter
): Promise<{
  items: InventoryItem[],
  hasNextPage: boolean,
  lastVisibleDocument?: QueryDocumentSnapshot
}> => {
  try {
    const userId = getUserId();

    let itemsQuery = query(getItemsCollection(), where('userId', '==', userId));

    // Apply equality filters
    if (filters) {
      if (filters.status && filters.status !== 'all') {
        itemsQuery = query(itemsQuery, where('status', '==', filters.status));
      }
      if (filters.itemType && filters.itemType !== 'all') {
        itemsQuery = query(itemsQuery, where('type', '==', filters.itemType));
      }
      if (filters.category && filters.category !== 'all') {
        itemsQuery = query(itemsQuery, where('category', '==', filters.category));
      }
      // Apply exact barcode match filter if provided
      if (filters.barcode) {
        itemsQuery = query(itemsQuery, where('barcode', '==', filters.barcode));
      }
    }

    let sortField = 'createdAt';
    let sortOrder: 'asc' | 'desc' = 'desc';

    // Apply active range filter and determine primary sort
    if (filters && activeRangeFilter === 'date' && filters.dateRange?.start && filters.dateRange?.end) {
      itemsQuery = query(itemsQuery,
        where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
        where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
      );
      sortField = 'createdAt';
      sortOrder = 'desc'; // Or configurable
    } else if (filters && activeRangeFilter === 'price' && filters.priceRange?.min !== undefined && filters.priceRange?.max !== undefined) {
      itemsQuery = query(itemsQuery,
        where('sellPerUnit', '>=', filters.priceRange.min),
        where('sellPerUnit', '<=', filters.priceRange.max)
      );
      sortField = 'sellPerUnit';
      sortOrder = 'asc'; // Example: sort by price ascending
    }

    // Apply sorting
    itemsQuery = query(itemsQuery, orderBy(sortField, sortOrder));
    // Add secondary sort if primary was not createdAt (and it's allowed by Firestore rules for the query)
    if (sortField === 'sellPerUnit') {
      itemsQuery = query(itemsQuery, orderBy('createdAt', 'desc'));
    }

    // Apply pagination (cursor or offset for first page)
    if (lastVisibleDoc) {
      itemsQuery = query(itemsQuery, startAfter(lastVisibleDoc), limit(pageSize));
    } else if (pageNumber > 1) {
      // Fallback to offset pagination if no cursor (e.g., jumping pages directly, though not ideal)
      // This part might need to be re-evaluated if purely cursor-based pagination is enforced.
      // For robust cursor pagination, we'd typically only fetch "next" or "previous" from a known cursor.
      // The current structure allows jumping to a page number, which can be tricky with cursors.
      // For simplicity, let's assume for now `pageNumber` is mainly for the initial load or if cursors are lost.
      // Fetching up to (pageNumber -1) * pageSize to find the startAfter doc for pageNumber.
      const previousPageLastItemQuery = query(
        getItemsCollection(), // This should also apply all filters and sorting to get the correct previous item.
        // Re-apply all filters and sorting as above to this query, then limit to (pageNumber - 1) * pageSize
        // then get the last doc. This is complex and inefficient.
        // A better approach for page jumps is to store cursors per page or disable direct page jumps.
        // For now, this offset logic is simplified and might not be robust with complex filters.
        // It's highly recommended to move to a purely cursor-based next/prev model.
        // The existing code has a more complex version of this; we'll keep it simple for now.
        // This simplified offset logic is for the sake of example; production would need robust cursor management.
        orderBy(sortField, sortOrder), // Ensure same order
        //Potentially add secondary sort if applicable
        limit((pageNumber - 1) * pageSize)
      );
       if (sortField === 'sellPerUnit') { // Re-apply secondary sort if it was there
         query(previousPageLastItemQuery, orderBy('createdAt', 'desc'));
       }

      // This simplified logic for offset will likely fail with Firestore if not careful with indexes and consistency
      // The original code had a more elaborate (but still potentially flawed) way to get the last doc of the previous page.
      // For now, focusing on cursor-based. If lastVisibleDoc is null and page > 1, it implies we need a more robust page jump.
      // The provided existing code for pageNumber > 1 already tries to fetch previous docs.
      // We'll rely on that logic by passing the main query object with filters applied.

      // Let's adapt the existing structure's approach:
      // If lastVisibleDoc is NOT provided (e.g. initial load of page > 1, or direct jump)
      if (!lastVisibleDoc && pageNumber > 1) {
        const prevQueryBase = query(getItemsCollection(), where('userId', '==', userId));
        // Apply ALL filters and sorting to prevQueryBase just like itemsQuery before pagination
        let prevItemsQuery = prevQueryBase;
        if (filters) {
          if (filters.status && filters.status !== 'all') {
            prevItemsQuery = query(prevItemsQuery, where('status', '==', filters.status));
          }
          if (filters.itemType && filters.itemType !== 'all') {
            prevItemsQuery = query(prevItemsQuery, where('type', '==', filters.itemType));
          }
          if (filters.category && filters.category !== 'all') {
            prevItemsQuery = query(prevItemsQuery, where('category', '==', filters.category));
          }
          // Apply exact barcode match filter if provided for previous page query
          if (filters.barcode) {
            prevItemsQuery = query(prevItemsQuery, where('barcode', '==', filters.barcode));
          }
        }
        if (filters && activeRangeFilter === 'date' && filters.dateRange?.start && filters.dateRange?.end) {
          prevItemsQuery = query(prevItemsQuery,
            where('createdAt', '>=', Timestamp.fromDate(filters.dateRange.start)),
            where('createdAt', '<=', Timestamp.fromDate(filters.dateRange.end))
          );
        } else if (filters && activeRangeFilter === 'price' && filters.priceRange?.min !== undefined && filters.priceRange?.max !== undefined) {
          prevItemsQuery = query(prevItemsQuery,
            where('sellPerUnit', '>=', filters.priceRange.min),
            where('sellPerUnit', '<=', filters.priceRange.max)
          );
        }
        prevItemsQuery = query(prevItemsQuery, orderBy(sortField, sortOrder));
        if (sortField === 'sellPerUnit') {
          prevItemsQuery = query(prevItemsQuery, orderBy('createdAt', 'desc'));
        }
        // End re-application of filters/sort

        const previousPageQuery = query(prevItemsQuery, limit((pageNumber - 1) * pageSize));
        const previousPageSnapshot = await getDocsFromServer(previousPageQuery); // Use server for consistency here

        if (!previousPageSnapshot.empty) {
          const lastDocOfPrevPage = previousPageSnapshot.docs[previousPageSnapshot.docs.length - 1];
          itemsQuery = query(itemsQuery, startAfter(lastDocOfPrevPage), limit(pageSize));
        } else {
          // No items on previous pages, so this page should be empty too if pageNumber > 1
          itemsQuery = query(itemsQuery, limit(pageSize)); // Effectively fetches page 1 if others empty
        }
      } else {
         // This is for page 1 initial load or cursor provided.
         itemsQuery = query(itemsQuery, limit(pageSize));
      }

    } else { // Page 1 or cursor-based
        itemsQuery = query(itemsQuery, limit(pageSize));
    }

    let snapshot;
    const sourceName = lastVisibleDoc ? 'cursor-based' : (pageNumber > 1 ? 'offset-based' : 'first page');

    try {
      snapshot = await getDocsFromCache(itemsQuery);
      if (snapshot.empty && snapshot.metadata.fromCache) {
        snapshot = await getDocsFromServer(itemsQuery);
      }
    } catch (cacheError) {
      console.warn('Could not fetch from cache, falling back to server:', cacheError);
      snapshot = await getDocsFromServer(itemsQuery);
    }

    const lastVisibleDocumentFromResult = snapshot.docs.length > 0
      ? snapshot.docs[snapshot.docs.length - 1]
      : undefined;

    const items = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        purchaseDate: data.purchaseDate?.toDate(),
        soldAt: data.soldAt?.toDate()
      } as InventoryItem;
    });

    const hasNextPage = items.length === pageSize;

    return { items, hasNextPage, lastVisibleDocument: lastVisibleDocumentFromResult };
  } catch (error) {
    console.error('Items could not be fetched:', error);
    throw error;
  }
};

/**
 * Get item by ID
 */
const _getItemById = async (id: string): Promise<InventoryItem | null> => {
  try {
    const docRef = getItemDocument(id);

    try {
      // Try to get from cache first
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const userId = getUserId();

        // Ensure the item belongs to the current user
        if (data.userId !== userId) {
          console.warn('Attempted to access item belonging to different user');
          return null;
        }

        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          purchaseDate: data.purchaseDate?.toDate(),
          soldAt: data.soldAt?.toDate()
        } as InventoryItem;
      }
    } catch (cacheError) {
      console.warn('Could not fetch item from cache, falling back to server:', cacheError);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const userId = getUserId();

        // Ensure the item belongs to the current user
        if (data.userId !== userId) {
          console.warn('Attempted to access item belonging to different user');
          return null;
        }

        return {
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          purchaseDate: data.purchaseDate?.toDate(),
          soldAt: data.soldAt?.toDate()
        } as InventoryItem;
      }
    }

    return null;
  } catch (error) {
    console.error('Item could not be fetched:', error);
    throw error;
  }
};

/**
 * Add new silver item
 */
const _addSilverItem = async (item: Omit<SilverItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<SilverItem> => {
  try {
    const userId = getUserId();

    // Ensure currency is set
    const costCurrency = item.costCurrency || 'TRY';
    const sellCurrency = item.sellCurrency || 'TRY';

    // Only fetch exchange rate if cost is in USD
    let exchangeRateData = {};
    if (costCurrency === 'USD' && item.purchaseDate) {
      try {
        const rate = await getExchangeRateForDate(item.purchaseDate);
        if (rate > 0) {
          exchangeRateData = { costExchangeRateToTry: rate };
        }
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
      }
    }

    // Calculate costPerUnit and sellPerUnit from weight and per-gram values
    const itemToAdd = {
      ...item,
      costCurrency,
      sellCurrency,
      ...exchangeRateData, // Only include exchange rate if available
      costPerUnit: item.costPerGram ? item.costPerGram * item.weight : 0,
      sellPerUnit: item.pricePerGram * item.weight,
      purchaseDate: item.purchaseDate || new Date(),
      userId,
      type: ItemType.SILVER,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(getItemsCollection(), itemToAdd);

    return {
      id: docRef.id,
      ...itemToAdd,
      createdAt: new Date(),
      updatedAt: new Date()
    } as SilverItem;
  } catch (error) {
    console.error('Item could not be added:', error);
    throw error;
  }
};

/**
 * Add new watch item
 */
const _addWatchItem = async (item: Omit<WatchItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<WatchItem> => {
  try {
    const userId = getUserId();

    // Ensure currency is set
    const costCurrency = item.costCurrency || 'TRY';
    const sellCurrency = item.sellCurrency || 'TRY';

    // Only fetch exchange rate if cost is in USD
    let exchangeRateData = {};
    if (costCurrency === 'USD' && item.purchaseDate) {
      try {
        const rate = await getExchangeRateForDate(item.purchaseDate);
        if (rate > 0) {
          exchangeRateData = { costExchangeRateToTry: rate };
        }
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
      }
    }

    const itemToAdd = {
      ...item,
      costCurrency,
      sellCurrency,
      ...exchangeRateData, // Only include exchange rate if available
      costPerUnit: item.costPerUnit || 0,
      sellPerUnit: item.sellPerUnit || 0,
      purchaseDate: item.purchaseDate || new Date(),
      userId,
      type: ItemType.WATCH,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(getItemsCollection(), itemToAdd);

    return {
      id: docRef.id,
      ...itemToAdd,
      createdAt: new Date(),
      updatedAt: new Date()
    } as WatchItem;
  } catch (error) {
    console.error('Item could not be added:', error);
    throw error;
  }
};

/**
 * Update item
 */
const _updateItem = async (id: string, item: Partial<Omit<BaseItem, 'id' | 'createdAt' | 'type'>>): Promise<void> => {
  try {
    // First verify that the item belongs to the current user
    const existingItem = await _getItemById(id);
    if (!existingItem) {
      throw new Error('Item not found or does not belong to the current user');
    }

    // For silver items, recalculate costPerUnit and sellPerUnit if necessary
    let updates = { ...item };

    if (existingItem.type === ItemType.SILVER) {
      const silverItem = existingItem as SilverItem;
      const silverUpdates = item as Partial<SilverItem>;

      const updatedWeight = silverUpdates.weight !== undefined ? silverUpdates.weight : silverItem.weight;
      const updatedCostPerGram = silverUpdates.costPerGram !== undefined 
        ? silverUpdates.costPerGram 
        : silverItem.costPerGram;
      const updatedPricePerGram = silverUpdates.pricePerGram !== undefined 
        ? silverUpdates.pricePerGram 
        : silverItem.pricePerGram || 0; // Default to 0 if undefined

      if (updatedWeight !== silverItem.weight || 
          updatedCostPerGram !== silverItem.costPerGram ||
          updatedPricePerGram !== silverItem.pricePerGram) {
        updates = {
          ...updates,
          costPerUnit: updatedCostPerGram ? updatedCostPerGram * updatedWeight : 0,
          sellPerUnit: updatedPricePerGram * updatedWeight
        };
      }
    }

    const docRef = getItemDocument(id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Item could not be updated:', error);
    throw error;
  }
};

/**
 * Delete item
 */
const _deleteItem = async (id: string): Promise<void> => {
  try {
    // First verify that the item belongs to the current user
    const existingItem = await _getItemById(id);
    if (!existingItem) {
      throw new Error('Item not found or does not belong to the current user');
    }

    const docRef = getItemDocument(id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Item could not be deleted:', error);
    throw error;
  }
};

/**
 * Find item by barcode
 */
const _internalFindItemByBarcode = async (barcode: string): Promise<InventoryItem | null> => {
  try {
    const userId = getUserId(); // Ensure userId is available
    
    const q = query(
      getItemsCollection(),
      where('userId', '==', userId),
      where('barcode', '==', barcode),
      limit(1)
    );

    let snapshot;

    try {
      // Try to get from cache first
      snapshot = await getDocsFromCache(q);

      if (snapshot.empty && snapshot.metadata.fromCache) {
        snapshot = await getDocsFromServer(q);
      }
    } catch (cacheError) {
      console.warn('Could not fetch from cache, falling back to server:', cacheError);
      snapshot = await getDocsFromServer(q);
    }

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        purchaseDate: data.purchaseDate?.toDate(),
        soldAt: data.soldAt?.toDate()
      } as InventoryItem;
    }

    return null;
  } catch (error) {
    console.error('Error searching item by barcode:', error);
    throw error;
  }
};

/**
 * Generate a unique SKU/name for an item
 * Format: {ItemTypeLetter}-{CategoryName}-{SequenceNumber}
 */
const _generateItemName = async (type: ItemType, categoryId: string): Promise<string> => {
  try {
    const userId = getUserId();

    // Get category name
    if (!db) throw new Error('Firestore yüklenemedi');
    const categoryDoc = await getDoc(doc(db, `categories/${categoryId}`));

    if (!categoryDoc.exists()) {
      throw new Error('Category not found');
    }

    const categoryName = categoryDoc.data().name;
    const typePrefix = type === ItemType.SILVER ? 'G' : 'S'; // G for Gümüş, S for Saat

    // Query to find items with same type and category
    const q = query(
      getItemsCollection(),
      where('userId', '==', userId),
      where('type', '==', type),
      where('category', '==', categoryId)
    );

    const snapshot = await getDocs(q);
    const itemCount = snapshot.size;

    // Format sequence number with leading zeros
    const sequenceNumber = (itemCount + 1).toString().padStart(3, '0');

    // Generate SKU: TypePrefix-CategoryName-SequenceNumber
    // Replace spaces with '-' in category name
    const formattedCategoryName = categoryName.replace(/\s+/g, '-').substring(0, 10);
    return `${typePrefix}-${formattedCategoryName}-${sequenceNumber}`;
  } catch (error) {
    console.error('Error generating item name:', error);
    throw error;
  }
};

/**
 * Mark an item as sold
 * @param id Item ID to mark as sold
 * @param options Optional parameters for the sale
 */
const _markItemAsSold = async (id: string, options?: { sellPerUnit?: number, sellCurrency?: Currency }): Promise<void> => {
  try {
    // First verify that the item belongs to the current user
    const existingItem = await _getItemById(id);
    if (!existingItem) {
      throw new Error('Ürün bulunamadı veya mevcut kullanıcıya ait değil');
    }

    // Prevent updating an already sold item
    if (existingItem.status === ItemStatus.SOLD) {
      throw new Error('Bu ürün zaten satılmış durumda');
    }

    const updates: Record<string, any> = {
      status: ItemStatus.SOLD,
      soldAt: serverTimestamp(),
    };

    // If override sell price is provided, use it
    if (options?.sellPerUnit !== undefined) {
      updates.sellPerUnit = options.sellPerUnit;
    }

    // If override sell currency is provided, use it
    if (options?.sellCurrency !== undefined) {
      updates.sellCurrency = options.sellCurrency;
    }

    // Get the current currency from the updates or the existing item
    const currentSellCurrency = options?.sellCurrency || existingItem.sellCurrency;

    // If the sell currency is USD, store the current exchange rate
    if (currentSellCurrency === 'USD') {
      try {
        const exchangeRate = await getCurrentExchangeRate();

        if (exchangeRate > 0) {
          updates.soldExchangeRateToTry = exchangeRate;
        }
      } catch (error) {
        console.warn('Exchange rate could not be fetched, continuing without it:', error);
      }
    }

    const docRef = getItemDocument(id);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp() // Ensure updatedAt is part of the updates for the item
    });

    // After successfully marking the item as sold, create a sales transaction
    try {
      const transactionPayload: Omit<SalesTransaction, 'id' | 'userId' | 'createdAt' | 'updatedAt'> = {
        itemId: existingItem.id,
        itemName: existingItem.name,
        itemBarcode: existingItem.barcode,
        itemCategory: existingItem.category, // This is category ID
        itemType: existingItem.type,
        saleDate: new Date(), // The date of the sale
        sellingPrice: (updates.sellPerUnit !== undefined) ? updates.sellPerUnit as number : existingItem.sellPerUnit,
        sellingCurrency: (updates.sellCurrency !== undefined) ? updates.sellCurrency as Currency : existingItem.sellCurrency,
        sellingExchangeRateToTry: updates.soldExchangeRateToTry as number | undefined, // Comes from updates if USD
        costPrice: existingItem.costPerUnit,
        costCurrency: existingItem.costCurrency,
        costExchangeRateToTry: existingItem.costExchangeRateToTry,
      };
      await createSalesTransaction(transactionPayload);
      console.log(`Transaction created for item ${id}`);
    } catch (transactionError) {
      console.error('Failed to create sales transaction:', transactionError);
      // Optionally, you might want to "rollback" the item status if transaction creation fails,
      // or log this for manual reconciliation. For now, just logging.
    }

  } catch (error) {
    console.error('Item could not be marked as sold:', error);
    throw error;
  }
};

/**
 * Set up real-time listener for inventory items changes
 * @param callback Function to call when items data changes
 * @param filters Optional filters (status, type)
 * @param limitCount Maximum number of items to listen to
 * @returns Unsubscribe function to stop listening
 */
const _subscribeToInventory = async (
  callback: (items: InventoryItem[]) => void,
  filters: {
    status?: ItemStatus | 'all';
    type?: ItemType | 'all';
  } = { status: 'all', type: 'all' },
  limitCount: number = 20
): Promise<Unsubscribe> => {
  if (!db) {
    throw new Error('Firestore yüklenemedi');
  }

  try {
    const userId = getUserId();
    const itemsCollection = getItemsCollection();

    // Start with base query including userId and ordering
    let queryConstraints = [
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    ];

    // Add type filter if specified and not 'all'
    if (filters.type && filters.type !== 'all') {
      queryConstraints.push(where('type', '==', filters.type));
    }

    // Add status filter if specified and not 'all'
    if (filters.status && filters.status !== 'all') {
      queryConstraints.push(where('status', '==', filters.status));
    }

    // Build the query with all constraints
    const itemsQuery = query(itemsCollection, ...queryConstraints);

    // Set up real-time listener
    return onSnapshot(
      itemsQuery,
      (snapshot) => {
        // Process items from the snapshot
        const items = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            purchaseDate: data.purchaseDate?.toDate(),
            soldAt: data.soldAt?.toDate()
          } as InventoryItem;
        });

        // Call the callback with the processed data
        callback(items);
      },
      (error) => {
        console.error('Error in inventory subscription:', error);
      }
    );
  } catch (error) {
    console.error('Error setting up inventory subscription:', error);
    throw new Error('Envanter aboneliği kurulurken bir hata oluştu');
  }
};

/**
 * Set up real-time listener for a single inventory item
 * @param itemId ID of the item to listen to
 * @param callback Function to call when the item data changes
 * @returns Unsubscribe function to stop listening
 */
const _subscribeToItem = async (
  itemId: string,
  callback: (item: InventoryItem | null) => void
): Promise<Unsubscribe> => {
  if (!db) {
    throw new Error('Firestore yüklenemedi');
  }

  try {
    const docRef = getItemDocument(itemId);

    // Set up real-time listener for the specific document
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const userId = getUserId();

          // Ensure the item belongs to the current user
          if (data.userId !== userId) {
            console.warn('Attempted to access item belonging to different user');
            callback(null);
            return;
          }

          const item = {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            purchaseDate: data.purchaseDate?.toDate(),
            soldAt: data.soldAt?.toDate()
          } as InventoryItem;

          callback(item);
        } else {
          // Document doesn't exist
          callback(null);
        }
      },
      (error) => {
        console.error(`Error in item subscription for id ${itemId}:`, error);
        callback(null);
      }
    );
  } catch (error) {
    console.error(`Error setting up item subscription for id ${itemId}:`, error);
    throw new Error('Ürün aboneliği kurulurken bir hata oluştu');
  }
};

/**
 * Check if a barcode already exists in the database
 * This function can be used to validate barcode uniqueness before saving an item
 */
const _checkBarcodeExists = async (barcode: string): Promise<boolean> => {
  try {
    const userId = getUserId();
    const q = query(
      getItemsCollection(),
      where('userId', '==', userId),
      where('barcode', '==', barcode),
      limit(1)
    );

    let snapshot;

    try {
      // Try to get from cache first for performance
      snapshot = await getDocsFromCache(q);

      if (snapshot.empty && snapshot.metadata.fromCache) {
        // Cache miss, check server
        snapshot = await getDocsFromServer(q);
      }
    } catch (cacheError) {
      // If cache fails, fall back to server
      snapshot = await getDocsFromServer(q);
    }

    // If the snapshot is not empty, the barcode exists
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking barcode existence:', error);
    throw error;
  }
};

/**
 * Find the last (highest) sequence number used in barcodes for a specific item type.
 * Barcode Format: TGS-<TypePrefix>-NNNN
 */
const _findLastBarcodeSequenceByType = async (itemType: ItemType): Promise<number> => {
  try {
    const userId = getUserId();
    const typePrefix = itemType === ItemType.SILVER ? 'S' : 'W';
    const barcodePrefix = `TGS-${typePrefix}-`; // e.g., "TGS-S-"

    // Query items of the specific type for the current user
    // Order by barcode descending to get the highest sequence first
    // We might need a more complex query if barcodes aren't perfectly sequential or have gaps
    const q = query(
      getItemsCollection(),
      where('userId', '==', userId),
      where('type', '==', itemType),
      // Firestore doesn't support ordering by substring or extracted number directly.
      // Fetch all matching the prefix and find max manually, or order descending by barcode string.
      // Ordering descending by the full barcode string *should* work if timestamps were used before,
      // but for TGS-S-NNNN, it should place highest NNNN first.
      where('barcode', '>=', barcodePrefix),
      where('barcode', '<=', barcodePrefix + '\uf8ff'),
      orderBy('barcode', 'desc'),
      limit(1) // Get only the highest one
    );

    let querySnapshot;
    try {
      // Try cache first
      querySnapshot = await getDocsFromCache(q);
      if (querySnapshot.empty && querySnapshot.metadata.fromCache) {
        querySnapshot = await getDocsFromServer(q);
      }
    } catch (e) {
      querySnapshot = await getDocsFromServer(q);
    }

    if (querySnapshot.empty) {
      // No barcodes found for this type yet
      return 0;
    }

    // Get the barcode string from the found document
    const lastBarcode = querySnapshot.docs[0].data().barcode as string;

    // Extract the sequence number part
    const parts = lastBarcode.split('-');
    if (parts.length === 3 && parts[0] === 'TGS' && parts[1] === typePrefix) {
      const sequenceStr = parts[2];
      if (/^\d{4}$/.test(sequenceStr)) {
        return parseInt(sequenceStr, 10);
      }
    }

    // If the highest barcode found doesn't match the expected format, return 0
    console.warn(`Highest barcode found (${lastBarcode}) doesn't match expected format TGS-${typePrefix}-NNNN. Starting sequence from 0.`);
    return 0;

  } catch (error) {
    console.error('Error finding last barcode sequence:', error);
    // Re-throw or return 0 to indicate failure? Returning 0 might lead to duplicates if others exist.
    // Re-throwing might be safer, forcing the generator to use the error fallback.
    throw error;
  }
};

// Expose the API through auth wrapper
export const getAllItems = withAuthentication(_getAllItems);
export const getItemById = withAuthentication(_getItemById);
export const addSilverItem = withAuthentication(_addSilverItem);
export const addWatchItem = withAuthentication(_addWatchItem);
export const updateItem = withAuthentication(_updateItem);
export const deleteItem = withAuthentication(_deleteItem);
export const findItemByBarcode = withAuthentication(_internalFindItemByBarcode);
export const generateItemName = withAuthentication(_generateItemName);
export const markItemAsSold = withAuthentication(_markItemAsSold);
export const subscribeToInventory = withAuthentication(_subscribeToInventory);
export const subscribeToItem = withAuthentication(_subscribeToItem);
export const checkBarcodeExists = withAuthentication(_checkBarcodeExists);
export const findLastBarcodeSequenceByType = withAuthentication(_findLastBarcodeSequenceByType);
export { _internalFindItemByBarcode as findItemByBarcodeInternal }; 